// §4.9 Promises (`/planning/promises`, P3-J-1, D-463, D-464, D-474): the
// facility's capable-to-promise records with row 77's filters in one
// `FilterBar`, the status chips, the drawer at `?promise=<id>` and the CTP
// dialog at `?check=1`, which `?line=<id>` binds to one order line so the
// dialog can commit onto it. Read on `planning.read`; Check a date and the
// drawer's Withdraw on `planning.promise.commit`. On a phone the table is
// cards, the drawer a sheet and the dialog full-screen.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { FilterBar, type FilterChip } from "../../../../components/filter-bar";
import { Select } from "../../../../components/select";
import { EmptyState, ErrorState } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { CtpDialog } from "../components/ctp-dialog";
import { PlanningHeader } from "../components/planning-header";
import { ProductPicker } from "../components/product-picker";
import { PromiseCustomerPicker } from "../components/promise-customer-picker";
import {
  BaseQty,
  PromisePathChip,
  PromiseStatusChip,
  promiseProductText,
} from "../components/promise-bits";
import { PromiseDrawer } from "../components/promise-drawer";
import { usePlanningPermissions } from "../hooks/permissions";
import { useProductIndex } from "../hooks/planning-queries";
import {
  PROMISE_STATUSES,
  usePromiseCustomerOrders,
  usePromises,
  type CtpPromise,
} from "../hooks/promise-queries";
import { formatDate, useOffline } from "../lib/planning-lib";
import {
  activePromiseFilters,
  compactPromisesSearch,
  promiseFiltersOf,
  validatePromisesSearch,
  type PromisesSearch,
} from "../lib/promise-lib";
import { usePlanningFacility } from "../planning-layout";

export function PlanningPromisesScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const canCommit = perms.has("planning.promise.commit");
  const offline = useOffline();
  const lang = i18n.language;

  const rawSearch = useSearch({ strict: false });
  const search = useMemo(
    () => validatePromisesSearch(rawSearch as Record<string, unknown>),
    [rawSearch],
  );
  const filters = useMemo(() => promiseFiltersOf(search), [search]);
  const setSearch = (next: PromisesSearch, replace = true) => {
    void navigate({ to: "/planning/promises", search: compactPromisesSearch(next), replace });
  };
  const patch = (changes: Partial<PromisesSearch>) => {
    setSearch({ ...search, ...changes });
  };

  // Cursor paging; a filter or facility change starts again from page one.
  const pageKey = `${facility.id}|${JSON.stringify(filters)}`;
  const [paging, setPaging] = useState<{ key: string; cursors: (string | undefined)[] }>({
    key: pageKey,
    cursors: [undefined],
  });
  if (paging.key !== pageKey) setPaging({ key: pageKey, cursors: [undefined] });
  const cursors = paging.key === pageKey ? paging.cursors : [undefined];
  const page = cursors.length - 1;
  const list = usePromises(facility.id, filters, cursors[page]);
  const rows = useMemo(() => list.data?.items ?? [], [list.data]);

  const authError = list.error instanceof UnauthorizedError ? list.error : undefined;
  useEffect(() => {
    if (authError !== undefined) void navigate({ to: "/login" });
  }, [authError, navigate]);

  const [pickedCustomer, setPickedCustomer] = useState<{ id: string; name: string } | null>(null);
  const customerOrders = usePromiseCustomerOrders(facility.id, search.customer);

  const products = useProductIndex([
    ...rows.map((r) => r.product_id),
    ...(search.product !== undefined ? [search.product] : []),
  ]);

  const columns: DataTableColumn<CtpPromise>[] = [
    {
      key: "promise_no",
      header: t("planning.promises.columns.promiseNo"),
      kind: "id",
      render: (row) => <span data-testid="planning-promise-no">{row.promise_no}</span>,
    },
    {
      key: "product",
      header: t("planning.promises.columns.product"),
      phonePriority: 1,
      render: (row) =>
        promiseProductText(row.product_label, products.byId.get(row.product_id)) ?? "",
    },
    {
      key: "qty",
      header: t("planning.promises.columns.qty"),
      kind: "qty",
      phonePriority: 3,
      render: (row) => <BaseQty qty={row.qty} product={products.byId.get(row.product_id)} />,
    },
    {
      key: "customer",
      header: t("planning.promises.columns.customer"),
      phonePriority: 4,
      render: (row) =>
        row.customer_label ?? (row.customer_id !== null ? t("planning.promises.customer") : ""),
    },
    {
      key: "order",
      header: t("planning.promises.columns.order"),
      render: (row) =>
        row.order_label ?? (row.sales_order_id !== null ? t("planning.promises.salesOrder") : ""),
    },
    {
      key: "requested",
      header: t("planning.promises.columns.requested"),
      kind: "date",
      render: (row) => formatDate(row.requested_date, lang),
    },
    {
      key: "promised",
      header: t("planning.promises.columns.promised"),
      kind: "date",
      phonePriority: 2,
      render: (row) => (
        <span data-testid="planning-promise-promised" data-date={row.promised_date}>
          {formatDate(row.promised_date, lang)}
        </span>
      ),
    },
    {
      key: "projected",
      header: t("planning.promises.columns.projected"),
      kind: "date",
      render: (row) =>
        row.current_projected_date === null ? (
          ""
        ) : (
          <span
            className={row.current_projected_date > row.promised_date ? "planning-bad" : undefined}
            data-date={row.current_projected_date}
          >
            {formatDate(row.current_projected_date, lang)}
          </span>
        ),
    },
    {
      key: "path",
      header: t("planning.promises.columns.path"),
      render: (row) => <PromisePathChip path={row.fulfillment_path} />,
    },
    {
      key: "status",
      header: t("planning.promises.columns.status"),
      kind: "status",
      render: (row) => <PromiseStatusChip status={row.status} />,
    },
    {
      key: "risk",
      header: t("planning.promises.columns.risk"),
      render: (row) =>
        row.risk_reason === null ? (
          ""
        ) : (
          <span className="planning-bad" data-testid="planning-promise-risk">
            {t(`planning.promises.risk.${row.risk_reason}`)}
          </span>
        ),
    },
    {
      key: "source",
      header: t("planning.promises.columns.source"),
      render: (row) => t(`planning.promises.source.${row.source}`),
    },
  ];

  const openPromise = (id: string) => {
    setSearch({ ...search, promise: id }, false);
  };
  const closePromise = () => {
    setSearch({ ...search, promise: undefined });
  };
  const openCheck = () => {
    setSearch({ ...search, check: "1" }, false);
  };
  const closeCheck = () => {
    setSearch({ ...search, check: undefined, line: undefined });
  };

  const header = (
    <PlanningHeader
      title={t("planning.promises.title")}
      actions={
        canCommit ? (
          <Button
            variant="primary"
            disabled={offline}
            data-testid="planning-promises-check"
            onClick={openCheck}
          >
            {t("planning.promises.checkDate")}
          </Button>
        ) : undefined
      }
    />
  );

  const filtered = activePromiseFilters(search);
  const productName = (() => {
    if (search.product === undefined) return undefined;
    const p = products.byId.get(search.product);
    return p !== undefined ? `${p.sku} · ${p.name}` : "…";
  })();
  // A chip names the customer and order it filters by: the one just picked,
  // else the label module 09 served on a listed row, else the generic chip.
  const customerName =
    search.customer === undefined
      ? undefined
      : pickedCustomer?.id === search.customer
        ? pickedCustomer.name
        : (rows.find((r) => r.customer_id === search.customer)?.customer_label ?? undefined);
  const orderName =
    search.order === undefined
      ? undefined
      : ((customerOrders.data ?? []).find((o) => o.id === search.order)?.order_no ??
        rows.find((r) => r.sales_order_id === search.order)?.order_label ??
        undefined);
  const chips: FilterChip[] = filtered.map((key) => {
    const label =
      key === "status"
        ? t("planning.promises.filterTag.status", {
            status: t(`planning.promises.status.${search.status ?? "quoted"}`),
          })
        : key === "from"
          ? t("planning.promises.filterTag.from", { date: formatDate(search.from ?? "", lang) })
          : key === "to"
            ? t("planning.promises.filterTag.to", { date: formatDate(search.to ?? "", lang) })
            : key === "product"
              ? t("planning.promises.filterTag.product", { product: productName ?? "" })
              : key === "customer" && customerName !== undefined
                ? t("planning.promises.filterTag.customerNamed", { customer: customerName })
                : key === "order" && orderName !== undefined
                  ? t("planning.promises.filterTag.orderNamed", { order: orderName })
                  : t(`planning.promises.filterTag.${key}`);
    return {
      key,
      label,
      onRemove: () => {
        patch({ [key]: undefined });
      },
    };
  });

  if (list.error !== null && authError === undefined && list.data === undefined) {
    return (
      <section className="planning-screen" data-testid="planning-promises">
        {header}
        <ErrorState
          title={t("planning.promises.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void list.refetch()}
          requestId={requestIdOf(list.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </section>
    );
  }

  const emptyState =
    filtered.length > 0 ? (
      <EmptyState
        title={t("planning.promises.filteredEmptyTitle")}
        body={t("planning.promises.filteredEmptyBody", {
          filters: filtered.map((k) => t(`planning.promises.filters.${k}`)).join(", "),
        })}
        ctaLabel={t("planning.common.clearFilters")}
        onCta={() => {
          setSearch({});
        }}
      />
    ) : (
      <EmptyState
        title={t("planning.promises.emptyTitle")}
        body={t("planning.promises.emptyBody")}
      />
    );

  return (
    <section className="planning-screen" data-testid="planning-promises">
      {header}
      <FilterBar
        data-testid="planning-promises-filters"
        filters={{
          inline: (
            <Field label={t("planning.promises.filters.status")}>
              <Select
                value={search.status ?? ""}
                data-testid="planning-promises-filter-status"
                onChange={(e) => {
                  patch({
                    status: (e.target.value || undefined) as PromisesSearch["status"],
                  });
                }}
              >
                <option value="">{t("planning.promises.filters.live")}</option>
                {PROMISE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`planning.promises.status.${s}`)}
                  </option>
                ))}
              </Select>
            </Field>
          ),
          more: (
            <>
              <Field label={t("planning.promises.filters.from")}>
                <input
                  type="date"
                  value={search.from ?? ""}
                  data-testid="planning-promises-filter-from"
                  onChange={(e) => {
                    patch({ from: e.target.value || undefined });
                  }}
                />
              </Field>
              <Field label={t("planning.promises.filters.to")}>
                <input
                  type="date"
                  value={search.to ?? ""}
                  data-testid="planning-promises-filter-to"
                  onChange={(e) => {
                    patch({ to: e.target.value || undefined });
                  }}
                />
              </Field>
              <ProductPicker
                label={t("planning.promises.filters.product")}
                data-testid="planning-promises-filter-product"
                onPick={(p) => {
                  patch({ product: p.id });
                }}
              />
              <PromiseCustomerPicker
                label={t("planning.promises.filters.customer")}
                data-testid="planning-promises-filter-customer"
                onPick={(c) => {
                  setPickedCustomer({ id: c.id, name: c.display_name });
                  // An order belongs to one customer, so a new customer drops it.
                  patch({ customer: c.id, order: undefined });
                }}
              />
              <Field label={t("planning.promises.filters.order")}>
                <Select
                  value={search.order ?? ""}
                  disabled={search.customer === undefined}
                  data-testid="planning-promises-filter-order"
                  onChange={(e) => {
                    patch({ order: e.target.value || undefined });
                  }}
                >
                  <option value="">
                    {search.customer === undefined
                      ? t("planning.promises.filters.orderNeedsCustomer")
                      : t("planning.promises.filters.anyOrder")}
                  </option>
                  {(customerOrders.data ?? []).map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.order_no}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          ),
          label: t("planning.common.filtersCount", { count: filtered.length }),
          title: t("planning.common.filters"),
          doneLabel: t("planning.common.done"),
        }}
        chips={chips}
        removeLabel={(chip) => t("planning.common.removeFilter", { filter: chip.label })}
        clearAll={{
          label: t("planning.common.clearFilters"),
          onClear: () => {
            setSearch({});
          },
        }}
      />
      <Card>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          rowTestId="planning-promise-row"
          rowProps={(r) => ({
            "data-promise-id": r.id,
            "data-promise-no": r.promise_no,
            "data-status": r.status,
            "data-path": r.fulfillment_path,
          })}
          onRowClick={(r) => {
            openPromise(r.id);
          }}
          loading={list.isPending}
          loadingLabel={t("planning.promises.loading")}
          caption={t("planning.promises.title")}
          fit
          empty={emptyState}
          error={
            list.error !== null && authError === undefined ? (
              <ErrorState
                title={t("planning.promises.loadError")}
                retryLabel={t("planning.common.retry")}
                onRetry={() => void list.refetch()}
                requestId={requestIdOf(list.error)}
                requestIdLabel={t("planning.common.requestId")}
              />
            ) : undefined
          }
          pager={{
            range: t("planning.common.page", { page: page + 1 }),
            page: page + 1,
            onPage: (n) => {
              if (n > page + 1 && list.data?.next_cursor) {
                const next = list.data.next_cursor;
                setPaging({ key: pageKey, cursors: [...cursors, next] });
              } else if (n < page + 1) setPaging({ key: pageKey, cursors: cursors.slice(0, -1) });
            },
            prevLabel: t("planning.common.previous"),
            nextLabel: t("planning.common.next"),
            hasPrev: page > 0,
            hasNext: (list.data?.next_cursor ?? null) !== null,
          }}
        />
      </Card>
      {search.promise !== undefined ? (
        <PromiseDrawer key={search.promise} id={search.promise} onClose={closePromise} />
      ) : null}
      {search.check !== undefined ? (
        <CtpDialog
          facilityId={facility.id}
          {...(search.line !== undefined ? { line: { id: search.line } } : {})}
          onClose={closeCheck}
        />
      ) : null}
    </section>
  );
}
